/**
 * Telling "this page's code went away in a deploy" apart from every other failure.
 *
 * Each image carries only its own content-hashed chunks, so a tab or an installed
 * app opened before a deploy asks for chunks nginx no longer has the first time
 * it opens another page. Reloading picks up the new version and the route
 * survives it (auth/returnPath.ts). An installed app has no reload button and
 * can stay open for days, which is why this does not wait for the member to
 * find one.
 *
 * No React here, for the same reason as connectivity.ts: it is tested under node.
 */

import { quietly, type PathStorage } from '../auth/returnPath';

/** Chrome, Firefox and Safari's wording for a failed dynamic import, then Vite's for CSS. */
const MODULE_LOAD_FAILURE =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export const isModuleLoadError = (error: unknown): boolean =>
  error instanceof Error && MODULE_LOAD_FAILURE.test(error.message);

/** Per tab, like the return path: it has to outlive exactly one reload. */
const RELOADED_AT_KEY = 'mlp_stale_bundle_reloaded_at';

/**
 * Covers a reload plus the sign-in round trip after it. A second failure inside
 * it is not a stale bundle but a broken one, and reloading again would loop.
 */
const RELOAD_GUARD_MS = 30_000;

/**
 * Long enough for a slow mobile connection. A stalled one must not hold the
 * spinner, which has no buttons, for as long as the browser's own timeout.
 */
const PROBE_TIMEOUT_MS = 5_000;

/** An answer that has not come in time is no answer. */
const withinDeadline = (answer: Promise<boolean>): Promise<boolean> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
    answer.then(
      (ok) => {
        clearTimeout(timer);
        resolve(ok);
      },
      () => {
        clearTimeout(timer);
        resolve(false);
      },
    );
  });

/**
 * `unreachable`: the platform did not answer, so the chunk may well still exist
 * and the member gets the connectivity message, not the device's offline page.
 * `failed`: it answered, but a reload was just tried or cannot be guarded.
 */
export type Recovery = 'reloading' | 'unreachable' | 'failed';

export interface RecoveryDeps {
  /** Does the platform answer at all? */
  probe: () => Promise<boolean>;
  /** Null when the browser refuses storage: then there is no guard, so no reload. */
  storage: PathStorage | null;
  now: () => number;
  reload: () => void;
}

export const recoverFromStaleBundle = async (deps: RecoveryDeps): Promise<Recovery> => {
  // A probe that cannot even start is no answer either; thrown from here it
  // would leave the spinner up for good.
  const answer = quietly(() => deps.probe(), Promise.resolve(false));
  if (!(await withinDeadline(answer))) return 'unreachable';
  const { storage } = deps;
  if (storage === null) return 'failed';

  const last = Number(quietly(() => storage.getItem(RELOADED_AT_KEY), null));
  if (last > 0 && deps.now() - last < RELOAD_GUARD_MS) return 'failed';

  const stamped = quietly(() => {
    storage.setItem(RELOADED_AT_KEY, String(deps.now()));
    return true;
  }, false);
  if (!stamped) return 'failed';

  deps.reload();
  return 'reloading';
};

export const browserRecoveryDeps = (): RecoveryDeps => ({
  // index.html, past every cache: nginx serves it for `/`, and it is the file a
  // reload would fetch next. Where the browser has AbortSignal.timeout (Chrome
  // 103, Safari 16), the signal also cancels the request the deadline gave up on.
  probe: () =>
    window
      .fetch('/', {
        method: 'HEAD',
        cache: 'no-store',
        signal: window.AbortSignal?.timeout?.(PROBE_TIMEOUT_MS),
      })
      .then(
      (response) => response.ok,
      () => false,
    ),
  storage: quietly<PathStorage | null>(() => window.sessionStorage, null),
  now: Date.now,
  reload: () => window.location.reload(),
});
